import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, ChevronDown, Zap, Sparkles, Crown, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { TrialInfoModal } from './TrialInfoModal';

// ─── Types ──────────────────────────────────────────────────────────────────

interface PlanFeature {
  text: string;
  included: boolean | string;
}

interface PlanPricing {
  monthly: number;
  quarterly: number;
  yearly: number;
}

interface Plan {
  key: string;
  name: string;
  icon: typeof Zap;
  description: string;
  features: PlanFeature[];
  cta: string;
  href: string;
  highlighted: boolean;
  badge?: string;
  isExternal?: boolean;
  accentColor: string;
}

type BillingCycle = 'monthly' | 'quarterly' | 'yearly';
type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'INR' | 'AED' | 'CAD' | 'AUD' | 'JPY';

interface CurrencyConfig {
  code: CurrencyCode;
  symbol: string;
  label: string;
  locale: string;
}

// ─── Data (preserved from existing) ─────────────────────────────────────────

const FIXED_PRICING: Record<CurrencyCode, Record<string, PlanPricing>> = {
  INR: {
    basic: { monthly: 999, quarterly: 2697, yearly: 10189 },
    basic_plus: { monthly: 2499, quarterly: 6747, yearly: 25489 },
    pro: { monthly: 4999, quarterly: 13497, yearly: 50989 },
  },
  USD: {
    basic: { monthly: 10, quarterly: 27, yearly: 102 },
    basic_plus: { monthly: 25, quarterly: 67, yearly: 255 },
    pro: { monthly: 50, quarterly: 135, yearly: 510 },
  },
  EUR: {
    basic: { monthly: 10, quarterly: 27, yearly: 102 },
    basic_plus: { monthly: 25, quarterly: 67, yearly: 255 },
    pro: { monthly: 50, quarterly: 135, yearly: 510 },
  },
  AED: {
    basic: { monthly: 40, quarterly: 108, yearly: 408 },
    basic_plus: { monthly: 100, quarterly: 270, yearly: 1020 },
    pro: { monthly: 200, quarterly: 540, yearly: 2040 },
  },
  GBP: {
    basic: { monthly: 8, quarterly: 22, yearly: 82 },
    basic_plus: { monthly: 20, quarterly: 54, yearly: 204 },
    pro: { monthly: 40, quarterly: 108, yearly: 408 },
  },
  CAD: {
    basic: { monthly: 14, quarterly: 38, yearly: 143 },
    basic_plus: { monthly: 34, quarterly: 92, yearly: 347 },
    pro: { monthly: 68, quarterly: 184, yearly: 694 },
  },
  AUD: {
    basic: { monthly: 15, quarterly: 41, yearly: 153 },
    basic_plus: { monthly: 38, quarterly: 103, yearly: 388 },
    pro: { monthly: 77, quarterly: 208, yearly: 785 },
  },
  JPY: {
    basic: { monthly: 1500, quarterly: 4050, yearly: 15300 },
    basic_plus: { monthly: 3750, quarterly: 10125, yearly: 38250 },
    pro: { monthly: 7500, quarterly: 20250, yearly: 76500 },
  },
};

const CURRENCIES: CurrencyConfig[] = [
  { code: 'USD', symbol: '$', label: 'USD ($)', locale: 'en-US' },
  { code: 'EUR', symbol: '\u20ac', label: 'EUR (\u20ac)', locale: 'de-DE' },
  { code: 'GBP', symbol: '\u00a3', label: 'GBP (\u00a3)', locale: 'en-GB' },
  { code: 'INR', symbol: '\u20b9', label: 'INR (\u20b9)', locale: 'en-IN' },
  { code: 'AED', symbol: 'AED', label: 'AED', locale: 'ar-AE' },
  { code: 'CAD', symbol: 'CA$', label: 'CAD (CA$)', locale: 'en-CA' },
  { code: 'AUD', symbol: 'A$', label: 'AUD (A$)', locale: 'en-AU' },
  { code: 'JPY', symbol: '\u00a5', label: 'JPY (\u00a5)', locale: 'ja-JP' },
];

function detectUserCurrency(): CurrencyCode {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const lang = navigator.language || 'en-US';
    if (timezone.includes('Kolkata') || timezone.includes('Mumbai') || lang.includes('IN')) return 'INR';
    if (timezone.includes('Dubai') || lang.includes('AE')) return 'AED';
    if (timezone.includes('Europe') && !timezone.includes('London')) return 'EUR';
    if (timezone.includes('London') || lang.includes('GB')) return 'GBP';
    if (timezone.includes('Australia') || lang.includes('AU')) return 'AUD';
    if (timezone.includes('Toronto') || timezone.includes('Vancouver') || lang.includes('CA')) return 'CAD';
    if (timezone.includes('Tokyo') || lang.includes('JP')) return 'JPY';
  } catch {
    /* ignore */
  }
  return 'USD';
}

const plans: Plan[] = [
  {
    key: 'basic',
    name: 'Basic',
    icon: Zap,
    description: 'Perfect for getting started',
    features: [
      { text: '5 generations/month', included: true },
      { text: '1 platform per generation', included: true },
      { text: '1 variation per platform', included: true },
      { text: '1 workspace', included: true },
      { text: '5 saved prompts', included: true },
      { text: 'AI Profile Intelligence', included: true },
      { text: 'AI Learning System', included: true },
      { text: 'All tone options', included: true },
      { text: 'Post to LinkedIn, Instagram, Facebook', included: true },
      { text: 'Stock image suggestions', included: 'Add your own' },
      { text: 'AI image generation', included: false },
      { text: 'Service-specific posts', included: false },
      { text: 'Post to X (Twitter)', included: false },
    ],
    cta: 'Get Started',
    href: '/register',
    highlighted: false,
    accentColor: '#8A94A6',
  },
  {
    key: 'basic_plus',
    name: 'Basic+',
    icon: Sparkles,
    description: 'For growing creators',
    features: [
      { text: '15 generations/month', included: true },
      { text: '2 platforms per generation', included: true },
      { text: '2 variations per platform', included: true },
      { text: '3 workspaces', included: true },
      { text: '15 saved prompts', included: true },
      { text: 'AI Profile Intelligence', included: true },
      { text: 'AI Learning System', included: true },
      { text: 'All tone options', included: true },
      { text: 'Post to LinkedIn, Instagram, Facebook', included: true },
      { text: '3 stock images per generation', included: true },
      { text: 'AI image generation', included: true },
      { text: 'Service-specific posts', included: true },
      { text: 'Post to X (Twitter)', included: false },
    ],
    cta: 'Get Started',
    href: '/register',
    highlighted: false,
    accentColor: '#12263A',
  },
  {
    key: 'pro',
    name: 'Pro',
    icon: Crown,
    description: 'Full power for professionals',
    features: [
      { text: '50 generations/month', included: true },
      { text: 'All 4 platforms', included: true },
      { text: '3 variations per platform', included: true },
      { text: '8 workspaces', included: true },
      { text: '70 saved prompts', included: true },
      { text: 'AI Profile Intelligence', included: true },
      { text: 'AI Learning System', included: true },
      { text: 'All tone options', included: true },
      { text: 'Post to all platforms including X', included: true },
      { text: '5 stock images per generation', included: true },
      { text: 'AI image generation (Context-aware)', included: true },
      { text: 'Service-specific posts', included: true },
      { text: 'Post to X (Twitter)', included: true },
    ],
    cta: 'Get Started',
    href: '/register',
    highlighted: true,
    badge: 'Most Popular',
    accentColor: '#D97757',
  },
];

// ─── Component ──────────────────────────────────────────────────────────────

export function PricingSection() {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('USD');
  const [showTrialModal, setShowTrialModal] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('preferred_currency') as CurrencyCode | null;
    if (stored && FIXED_PRICING[stored]) {
      setCurrencyCode(stored);
    } else {
      setCurrencyCode(detectUserCurrency());
    }
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/tool');
      return;
    }
    setShowTrialModal(true);
  };

  const handleCurrencyChange = (code: CurrencyCode) => {
    setCurrencyCode(code);
    localStorage.setItem('preferred_currency', code);
  };

  const currentCurrency = useMemo(
    () => CURRENCIES.find((c) => c.code === currencyCode) || CURRENCIES[0],
    [currencyCode]
  );

  const getPrice = (planKey: string): string => {
    if (planKey === 'enterprise') return 'Custom';
    const pricing = FIXED_PRICING[currencyCode]?.[planKey];
    if (!pricing) return 'N/A';
    const price = pricing[billingCycle];
    try {
      return new Intl.NumberFormat(currentCurrency.locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    } catch {
      return `${currentCurrency.symbol}${price.toLocaleString()}`;
    }
  };

  const getPeriodLabel = () => {
    if (billingCycle === 'monthly') return 'month';
    if (billingCycle === 'quarterly') return 'quarter';
    return 'year';
  };

  const billingCycles: BillingCycle[] = ['monthly', 'quarterly', 'yearly'];

  return (
    <section
      id="pricing"
      className="relative overflow-hidden bg-paper"
      style={{
        paddingTop: 'clamp(40px, 5vh, 72px)',
        paddingBottom: 'clamp(40px, 5vh, 72px)',
      }}
    >
      <TrialInfoModal isOpen={showTrialModal} onClose={() => setShowTrialModal(false)} />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-6 text-center"
        >
          <p
            className="mb-2 font-mono uppercase text-steel-dark"
            style={{
              fontSize: 'clamp(0.8rem, 0.9vw, 1rem)',
              fontWeight: 600,
              letterSpacing: '0.2em',
            }}
          >
            Pricing
          </p>
          <h2
            className="mb-2 text-ink font-display"
            style={{
              fontSize: 'clamp(1.75rem, 3vw + 0.5rem, 2.75rem)',
              lineHeight: 1.1,
            }}
          >
            Choose Your Plan
          </h2>
          <p
            className="mb-6 text-steel-dark font-body"
            style={{
              fontSize: 'clamp(1rem, 1vw + 0.7rem, 1.25rem)',
              lineHeight: 1.5,
            }}
          >
            Start with a 15-day free trial. Upgrade when you're ready.
          </p>

          {/* Controls */}
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            {/* Billing toggle */}
            <div
              className="relative inline-flex items-center gap-1 p-1"
              style={{
                background: '#F7F6F2',
                border: '1px solid #D8DCE3',
              }}
            >
              {billingCycles.map((cycle) => (
                <button
                  key={cycle}
                  onClick={() => setBillingCycle(cycle)}
                  className={cn(
                    'relative z-10 px-4 py-2 text-sm font-medium font-body transition-all duration-200',
                    billingCycle === cycle
                      ? 'bg-ink text-white'
                      : 'text-steel-dark hover:text-ink'
                  )}
                >
                  {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                </button>
              ))}
            </div>

            {billingCycle !== 'monthly' && (
              <span
                className="text-sm font-semibold font-body text-ink"
              >
                {billingCycle === 'quarterly' ? 'Save 10%' : 'Save 15%'}
              </span>
            )}

            {/* Currency picker */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium font-body text-steel-dark transition-all"
                  style={{
                    background: '#F7F6F2',
                    border: '1px solid #D8DCE3',
                  }}
                >
                  {currencyCode}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="max-h-64 overflow-y-auto"
                style={{
                  background: '#FFFFFF',
                  border: '1px solid #D8DCE3',
                  color: '#12263A',
                }}
              >
                {CURRENCIES.map((c) => (
                  <DropdownMenuItem
                    key={c.code}
                    onClick={() => handleCurrencyChange(c.code)}
                    className="cursor-pointer"
                    style={{
                      background: c.code === currencyCode ? '#F7F6F2' : 'transparent',
                      color: c.code === currencyCode ? '#12263A' : '#5F6B7C',
                    }}
                  >
                    {c.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </motion.div>

        {/* Plan cards -- 3 in a row */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 max-w-5xl mx-auto">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            return (
              <motion.div
                key={plan.key}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.12,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{ y: -4, transition: { duration: 0.25 } }}
                className={cn(
                  'relative flex flex-col bg-white',
                  'border transition-all duration-300',
                  plan.highlighted
                    ? 'border-cargo md:scale-[1.02]'
                    : 'border-hairline hover:border-ink/20'
                )}
                style={{
                  padding: 'clamp(1.25rem, 2vw, 1.75rem)',
                  borderTop: plan.highlighted ? '3px solid #D97757' : undefined,
                }}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span
                      className="text-sm font-semibold font-body px-4 py-1.5 rounded-full whitespace-nowrap bg-cargo text-cargo-ink"
                    >
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Plan header */}
                <div className="text-center mb-4">
                  <div
                    className="w-9 h-9 mx-auto mb-2 flex items-center justify-center"
                    style={{
                      background: `${plan.accentColor}18`,
                      border: `1px solid ${plan.accentColor}30`,
                    }}
                  >
                    <Icon className="h-[18px] w-[18px]" style={{ color: plan.accentColor }} />
                  </div>
                  <h3
                    className="font-bold mb-0.5 text-ink font-display"
                    style={{
                      fontSize: '1.125rem',
                    }}
                  >
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1 mb-1">
                    <span
                      className="font-bold text-ink font-display"
                      style={{
                        fontSize: '1.5rem',
                      }}
                    >
                      {getPrice(plan.key)}
                    </span>
                    <span
                      className="text-sm text-steel-dark font-body"
                    >
                      / {getPeriodLabel()}
                    </span>
                  </div>
                  <p
                    className="text-sm text-steel-dark font-body"
                  >
                    {plan.description}
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-1.5 mb-4 flex-grow">
                  {plan.features.map((feature) => (
                    <li key={feature.text} className="flex items-start gap-2">
                      {typeof feature.included === 'boolean' ? (
                        feature.included ? (
                          <div
                            className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{
                              background: 'rgba(18,38,58,0.08)',
                            }}
                          >
                            <Check className="h-[9px] w-[9px]" style={{ color: '#12263A' }} />
                          </div>
                        ) : (
                          <div
                            className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ background: '#F7F6F2' }}
                          >
                            <X className="h-[9px] w-[9px]" style={{ color: '#8A94A6' }} />
                          </div>
                        )
                      ) : (
                        <div
                          className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: 'rgba(18,38,58,0.08)' }}
                        >
                          <span style={{ fontSize: 7, fontWeight: 700, color: '#12263A' }}>~</span>
                        </div>
                      )}
                      <span
                        className={cn(
                          'text-sm leading-snug font-body',
                          typeof feature.included === 'boolean' && !feature.included
                            ? 'text-steel-dark opacity-60'
                            : 'text-ink'
                        )}
                      >
                        {typeof feature.included === 'string'
                          ? `${feature.text}: ${feature.included}`
                          : feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={handleGetStarted}
                  className={cn(
                    'w-full py-2.5 text-sm font-semibold font-body mt-auto transition-all duration-300 rounded-md',
                    plan.highlighted
                      ? 'bg-cargo text-cargo-ink hover:opacity-90'
                      : 'border border-hairline text-ink hover:border-ink'
                  )}
                >
                  {plan.cta}
                </button>

                {/* Trial callout */}
                <p
                  className="text-center mt-3 text-xs font-body text-steel-dark"
                >
                  15-day free trial included
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Enterprise callout */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-6"
        >
          <p
            className="text-sm text-steel-dark mb-2 font-body"
          >
            Need unlimited generations for your team?
          </p>
          <a
            href="mailto:bd@summerhilltechnologies.com"
            className="inline-flex items-center gap-2 text-sm font-semibold font-body text-ink hover:text-cargo transition-colors"
          >
            <Building2 className="h-4 w-4" />
            Contact us for Enterprise pricing
          </a>
        </motion.div>
      </div>
    </section>
  );
}
